import React, { useEffect, useMemo, useState } from "react";
import { showError, showSuccess } from "toast/toast";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom"; // Import useParams from React Router
import { MessageSquare, ExternalLink, Building, FileText, User, Mail, Phone, Calendar } from 'lucide-react';
import { withIconStyle, iconLabelProps } from "components/ViewIcons";
import { getAuthCookie } from "../../utils/authCookies";

type VendorRequest = {
    id: string;
    vendor_name: string;
    category: string;
    recommendation?: string;
    already_work_with?: boolean;
    multiple_offices?: boolean;
    status: "Under Review" | "In Process" | "Approved" | "Pending" | "Rejected" | string;

    // Single-request API often returns these:
    submitted_by?: string;
    email?: string;
    phone?: string;
    website?: string;
    date?: string; // submission date
};

const STATUSES = ["Under Review", "In Process", "Approved", "Pending", "Rejected"] as const;

const show = (v: any) => (v === undefined || v === null || v === "" ? "---" : v);
const formatDate = (d?: string) =>
    d ? new Date(d).toLocaleDateString("en-US") : "---";

function yesNoPill(v?: boolean) {
    const yes = v === true;
    return (
        <span
            className={`badge border-radioses fs-13 px-3 ${yes ? "bg-success-subtle text-success-emphasis" : "bg-body-tertiary text-secondary-emphasis"
                }`}
        >
            {yes ? "Yes" : "No"}
        </span>
    );
}

function statusPill(status?: string) {
    const s = (status || "").toLowerCase();
    let cls = "bg-body-tertiary text-secondary-emphasis fs-13";
    if (s.includes("review")) cls = "bg-primary-subtle text-primary-emphasis fs-13";
    else if (s.includes("process")) cls = "bg-warning-subtle text-warning-emphasis fs-13";
    else if (s.includes("approved")) cls = "bg-success-subtle text-success-emphasis fs-13";
    else if (s.includes("pending")) cls = "bg-secondary-subtle text-secondary-emphasis fs-13";
    else if (s.includes("reject")) cls = "bg-danger-subtle text-danger-emphasis fs-13";

    return <span className={`badge border-radioses px-3 py-2 ${cls}`}>{status || "---"}</span>;
}

function statusDescription(status?: string) {
    const s = (status || "").toLowerCase();
    if (s.includes("review"))
        return { text: "This vendor request is currently being evaluated by the management team.", cls: "text-primary" };
    if (s.includes("process"))
        return { text: "This vendor request is currently being processed.", cls: "text-warning" };
    if (s.includes("approved"))
        return { text: "This vendor has been approved and added to our preferred vendor list.", cls: "text-success" };
    if (s.includes("pending"))
        return { text: "This vendor request is pending and waiting for action.", cls: "text-secondary" };
    if (s.includes("reject"))
        return { text: "This vendor request has been rejected.", cls: "text-danger" };
    return { text: "", cls: "text-muted" };
}

export default function VendorRequestView() {
    const { vendorId } = useParams<{ vendorId: string }>();
    const [data, setData] = useState<VendorRequest | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [nextStatus, setNextStatus] = useState<string>("");
    const navigate = useNavigate();
    const token = getAuthCookie("sessionToken");
    const auth = token ? { Authorization: `Bearer ${token}` } : undefined;

    // GET single request
    useEffect(() => {
        let aborted = false;
        async function run() {
            setLoading(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL || "https://localhost:5002"}/vendors/requests/${vendorId}`,
                    { headers: { "Content-Type": "application/json", ...(auth || {}) } }
                );
                const json = await res.json();
                if (!res.ok || json?.error) throw new Error(json?.message || json?.error || "Fetch failed");
                if (aborted) return;

                // Backend may return _id; normalize to id + flatten fields we need
                const normalized: VendorRequest = {
                    id: json?.id || json?._id || vendorId,
                    vendor_name: json?.vendor_name || "",
                    category: json?.category || "",
                    recommendation: json?.recommendation,
                    already_work_with: json?.already_work_with,
                    multiple_offices: json?.multiple_offices,
                    status: json?.status || "Under Review",
                    submitted_by: json?.submitted_by || json?.submittedBy,
                    email: json?.email,
                    phone: json?.phone,
                    website: json?.website || json?.contact?.website,
                    date: json?.date || json?.submitted_at || json?.created_at,
                };

                setData(normalized);
                setNextStatus(normalized.status);
            } catch (e: any) {
                console.error(e);
                showError?.(e.message || "Failed to load request");
            } finally {
                if (!aborted) setLoading(false);
            }
        }
        if (vendorId) run();
        return () => {
            aborted = true;
        };
    }, [vendorId]);

    async function saveStatus() {
        if (!data) return;
        setSaving(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL || "https://localhost:5002"}/vendors/requests/${data.id}/status`,
                {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json", ...(auth || {}) },
                    body: JSON.stringify({ status: nextStatus }),
                }
            );
            const json = await res.json();
            if (!res.ok || json?.error) throw new Error(json?.message || json?.error || "Update failed");

            setData((prev) => (prev ? { ...prev, status: nextStatus } : prev));
            setModalOpen(false);
            showSuccess?.("Status updated");
        } catch (e: any) {
            console.error(e);
            showError?.(e.message || "Failed to update status");
        } finally {
            setSaving(false);
        }
    }

    const desc = useMemo(() => statusDescription(data?.status), [data?.status]);

    return (
        <div className="content">
             <button
          type="button"
          className="btn btn-light d-inline-flex align-items-center gap-2 br-8 px-3 mb-3"
          onClick={() => navigate(-1)}
        >
          <i className="ri-arrow-left-line" />
          Back
        </button>
            {/* Top header shell you provided */}
            <div className="po-header mb-4 shadow-border-datatables">
                <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
                    {/* Left: title, category */}
                    <div>
                        <h3 className="mb-1">{show(data?.vendor_name)}</h3>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary-subtle text-primary border-radioses fs-13 p-2">
                                {show(data?.category).toString().replace(/^./, (c: string) => c.toUpperCase())}
                            </span>
                            <span>
                                {statusPill(data?.status)}
                            </span>
                        </div>
                    </div>

                    {/* Right: status pill */}
                    {/* <div className="d-flex align-items-center gap-2">
                        {statusPill(data?.status)}
                    </div> */}
                </div>
                <hr />
                <div className="row g-4">
                    {/* Left column */}
                    <div className="col-12 col-lg-6 mt-5">
                        <h5 className="d-flex align-items-center gap-2 wrap-anywhere mb-3 text-lg">
                            <div {...iconLabelProps(19)}>
                                <MessageSquare {...withIconStyle("#155dfc", 19)} />
                            </div>
                            Contact Information
                        </h5>

                        <div className="bg-borders-orders rounded-4 p-3 mb-4">
                            <div className="d-flex align-items-center gap-2">
                                <div {...iconLabelProps(19)}>
                                    <ExternalLink {...withIconStyle("#155dfc", 19)} />
                                </div>
                                <strong className="text-sm" style={{ color: "#155dfc" }}>Website:&nbsp;</strong>
                                <a href={data?.website || "#"} target="_blank" rel="noreferrer">
                                    {show((data?.website || "").replace(/^https?:\/\//, ""))}
                                </a>
                            </div>
                        </div>

                        <h5 className="d-flex align-items-center gap-2 wrap-anywhere mb-3 text-lg">
                            <div {...iconLabelProps(19)}>
                                <Building {...withIconStyle("#155dfc", 19)} />
                            </div>
                            Working Relationship
                        </h5>

                        <div className="bg-borders-orders rounded-4 p-3 mb-3 d-flex align-items-center justify-content-between">
                            <span className="text-sm">Already work with this vendor:</span>
                            {yesNoPill(data?.already_work_with)}
                        </div>

                        <div className="bg-borders-orders rounded-4 p-3 d-flex align-items-center justify-content-between">
                            <span className="text-sm">Serves multiple offices:</span>
                            {yesNoPill(data?.multiple_offices)}
                        </div>
                    </div>

                    {/* Right column */}
                    <div className="col-12 col-lg-6 mt-5">
                        <h5 className="d-flex align-items-center gap-2 wrap-anywhere mb-3 text-lg">
                            <div {...iconLabelProps(19)}>
                                <FileText {...withIconStyle("#155dfc", 19)} />
                            </div>
                            Submission Details
                        </h5>

                        <div className="bg-borders-orders rounded-4 p-3 mb-3">
                            <div className="d-flex align-items-center">  {/* <- center vertically */}
                                <span className="me-2 d-flex align-items-center">
                                    <User {...withIconStyle("#6B7280", 23)} />
                                </span>

                                <div className="d-flex flex-column justify-content-center">
                                    <div className="submittions text-muted mb-1">Submitted by:</div>
                                    <div className="fw-medium text-sm">{show(data?.submitted_by)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-borders-orders rounded-4 p-3 mb-3">
                            <div className="d-flex align-items-center">  {/* <- center vertically */}
                                <span className="me-2 d-flex align-items-center">
                                    <Mail {...withIconStyle("#6B7280", 18)} />
                                </span>

                                <div className="d-flex flex-column justify-content-center">
                                    <div className="submittions text-muted mb-1">Email:</div>
                                    <div className="fw-medium text-sm">{show(data?.email)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-borders-orders rounded-4 p-3 mb-3">
                            <div className="d-flex align-items-center">  {/* <- center vertically */}
                                <span className="me-2 d-flex align-items-center">
                                    <Phone {...withIconStyle("#6B7280", 18)} />
                                </span>

                                <div className="d-flex flex-column justify-content-center">
                                    <div className="submittions text-muted mb-1">Phone:</div>
                                    <div className="fw-medium text-sm">{show(data?.phone)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-borders-orders rounded-4 p-3 mb-3">
                            <div className="d-flex align-items-center">  {/* <- center vertically */}
                                <span className="me-2 d-flex align-items-center">
                                    <Calendar {...withIconStyle("#6B7280", 18)} />
                                </span>

                                <div className="d-flex flex-column justify-content-center">
                                    <div className="submittions text-muted mb-1">Submission date:</div>
                                    <div className="fw-medium text-sm">{show(data?.date)}</div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-4">
                            <h5 className="mb-3 text-lg">Request Status</h5>
                            <div className="bg-borders-orders rounded-4 p-3">
                                <div className="d-flex align-items-center justify-content-start gap-2 mb-2">
                                    <strong>Current status:</strong>
                                    {statusPill(data?.status)}
                                </div>
                                {desc.text ? <div className={desc.cls}>{desc.text}</div> : null}
                            </div>
                        </div>

                    </div>
                </div>

                {/* Request Status */}
                {/* <div className="mt-5">
                    <h5 className="mb-3">Request Status</h5>
                    <div className="bg-borders-orders rounded-3 p-3">
                        <div className="d-flex align-items-center justify-content-start gap-2 mb-2">
                            <strong>Current status:</strong>
                            {statusPill(data?.status)}
                        </div>
                        {desc.text ? <div className={desc.cls}>{desc.text}</div> : null}
                    </div>
                </div> */}

                {/* Recommendation */}
                <hr />
                <div className="mt-3 mb-4">
                    <h5 className="mb-3 text-lg">Why This Vendor is Recommended</h5>
                    <div className="p-4 rounded-4" style={{ background: "#eef5ff", border: "1px solid #cfe0ff" }}>
                        {show(data?.recommendation)}
                    </div>
                </div>
                <hr />
                {/* Footer actions */}
                <div className="mt-4">
                    <button
                        className="btn btn-primary"
                        onClick={() => setModalOpen(true)}
                        disabled={loading || !data}
                    >
                        Edit Request
                    </button>
                </div>

                {/* Modal: Edit Status */}
                {modalOpen && (
                    <>
                        {/* Modal */}
                        <div
                            className="modal fade show d-block"
                            tabIndex={-1}
                            role="dialog"
                            style={{ display: "block", zIndex: 1060 }}   // <- higher than backdrop
                            onClick={() => setModalOpen(false)}          // click outside closes
                        >
                            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Edit Request Status</h5>
                                        <button type="button" className="btn-close" onClick={() => setModalOpen(false)} />
                                    </div>

                                    <div className="modal-body">
                                        <label className="form-label">Update Status</label>
                                        <div className="position-relative">
                                            <select
                                                className="form-select"
                                                value={nextStatus}
                                                onChange={(e) => setNextStatus(e.target.value)}
                                            >
                                                {STATUSES.map((s) => (
                                                    <option key={s} value={s}>
                                                        {s}
                                                    </option>
                                                ))}
                                            </select>
                                            {/* purely decorative, don't block clicks */}
                                            <i
                                                className="bi bi-caret-down-fill position-absolute end-0 top-50 translate-middle-y me-3 text-muted"
                                                style={{ pointerEvents: "none" }}    // <- important
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button className="btn btn-light" onClick={() => setModalOpen(false)} disabled={saving}>
                                            Cancel
                                        </button>
                                        <button className="btn btn-primary" onClick={saveStatus} disabled={saving || !nextStatus}>
                                            {saving ? "Saving..." : "Save Changes"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Backdrop (lower z-index) */}
                        <div
                            className="modal-backdrop fade show"
                            style={{ zIndex: 1050 }}
                            onClick={() => setModalOpen(false)}
                        />
                    </>
                )}

            </div>
            <style>
                {`
        .bg-borders-orders {
          background: #fbf9fa;
        //   border: 1px solid #eef2f7;
        }
        .text-lg{
         font-size: 1.325rem;
        }
        .submittions{
            font-size: 1.175rem;
        }
            .border-radioses{
                border-radius: 16px;
            }
               /* Desktop (XL ≥ 1200px) and up only */
@media (min-width: 1200px) {
  .modal-dialog {
    position: fixed;   /* needed for top/left to take effect */
    top: 195px;
    left: 91px;
    margin: 0;         /* prevents Bootstrap centering margins */
  }
}

        `}
            </style>
        </div>
    );
}
